<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { EditorView, keymap, lineNumbers, drawSelection, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { linter as cmLinter } from '@codemirror/lint';
import { foldGutter, foldKeymap, bracketMatching, indentOnInput } from '@codemirror/language';
import { lintGutter } from '@codemirror/lint';
import { oneDark } from '@codemirror/theme-one-dark';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const container = ref(null);
let view = null;
let ignoreNextUpdate = false;

function isDarkMode() {
  return document.documentElement.classList.contains('dark');
}

const lightTheme = EditorView.theme({
  '&': {
    fontSize: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  '.cm-scroller': { overflow: 'auto', maxHeight: '240px' },
  '.cm-content': { padding: '8px 0', minHeight: '80px' },
  '.cm-line': { padding: '0 8px' },
  '.cm-gutters': {
    backgroundColor: '#f8f9fa',
    borderRight: '1px solid #e2e8f0',
    color: '#94a3b8',
  },
  '.cm-activeLineGutter': { backgroundColor: '#f1f5f9' },
  '.cm-activeLine': { backgroundColor: '#f8fafc' },
  '.cm-foldGutter': { width: '14px' },
  '.cm-tooltip': { fontFamily: 'ui-monospace, monospace', fontSize: '11px' },
  '.cm-lintRange-error': { backgroundImage: 'none', borderBottom: '2px solid #ef4444', textDecoration: 'none' },
  '.cm-diagnostic-error': { borderLeft: '3px solid #ef4444', padding: '2px 6px', color: '#ef4444' },
});

const darkTheme = EditorView.theme({
  '&': {
    fontSize: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  '.cm-scroller': { overflow: 'auto', maxHeight: '240px' },
  '.cm-content': { padding: '8px 0', minHeight: '80px' },
  '.cm-line': { padding: '0 8px' },
  '.cm-foldGutter': { width: '14px' },
  '.cm-tooltip': { fontFamily: 'ui-monospace, monospace', fontSize: '11px' },
  '.cm-lintRange-error': { backgroundImage: 'none', borderBottom: '2px solid #f87171', textDecoration: 'none' },
  '.cm-diagnostic-error': { borderLeft: '3px solid #f87171', padding: '2px 6px', color: '#f87171' },
}, { dark: true });

function templateAwareJsonLinter(view) {
  const text = view.state.doc.toString();
  if (!text.trim()) return [];
  const sanitized = text.replace(/\{\{[^}]+\}\}/g, 'null');
  try {
    JSON.parse(sanitized);
    return [];
  } catch (e) {
    const match = e.message.match(/position (\d+)/);
    const pos = match ? Math.min(parseInt(match[1]), view.state.doc.length) : 0;
    return [{ from: pos, to: Math.min(pos + 1, view.state.doc.length), severity: 'error', message: e.message }];
  }
}

function buildExtensions() {
  return [
    lineNumbers(),
    highlightActiveLine(),
    highlightActiveLineGutter(),
    drawSelection(),
    foldGutter(),
    bracketMatching(),
    indentOnInput(),
    history(),
    keymap.of([...defaultKeymap, ...historyKeymap, ...foldKeymap]),
    json(),
    cmLinter(templateAwareJsonLinter),
    lintGutter(),
    EditorView.lineWrapping,
    isDarkMode() ? [oneDark, darkTheme] : lightTheme,
    EditorView.updateListener.of(update => {
      if (update.docChanged) {
        ignoreNextUpdate = true;
        emit('update:modelValue', update.state.doc.toString());
      }
    }),
  ];
}

onMounted(() => {
  view = new EditorView({
    state: EditorState.create({
      doc: props.modelValue,
      extensions: buildExtensions(),
    }),
    parent: container.value,
  });
});

onUnmounted(() => {
  view?.destroy();
  view = null;
});

watch(() => props.modelValue, val => {
  if (!view || ignoreNextUpdate) {
    ignoreNextUpdate = false;
    return;
  }
  const current = view.state.doc.toString();
  if (current !== val) {
    view.dispatch({
      changes: { from: 0, to: current.length, insert: val },
    });
  }
});
</script>

<template>
  <div
    ref="container"
    class="rounded-lg border border-n-weak overflow-hidden focus-within:ring-1 focus-within:ring-n-brand focus-within:border-n-brand transition-colors"
  />
</template>
